import { getLocale, t, useI18n } from '../../i18n';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiAlertCircle, FiEdit2, FiSend } from 'react-icons/fi';
import {
  assignableRoles,
  canEditRolesOf,
  roleService,
  type CompanyRole,
  type StaffAccess,
} from '../../services/roleService';
import { isValidPhone, phoneDigits } from '../../services/staffInviteService';
import {
  canChangeAccountStatus,
  canEditDetailsOf,
  canResendInvite as mayResendInvite,
  hasIrishEroMinimumHoursIssue,
  isAwaitingSetup,
  missingProfileDetails,
  splitFullName,
  staffService,
  toBindingSelection,
  type ContractHoursBand,
  type EroMinimumHoursExceptionKind,
  type SiteBindingOptions,
  type SiteBindingSelection,
  type StaffLicenceRow,
  type StaffRow,
  type StaffStatus,
} from '../../services/staffService';
import { getAvatarAccentStyle } from '../../utils/avatarAccent';
import { sanitizeEmail, sanitizeText } from '../../utils/sanitize';
import ContractHoursSelect from './ContractHoursSelect';
import RoleChips from './RoleChips';
import RolePicker from './RolePicker';
import SiteBindingPicker from './SiteBindingPicker';
import { contractBandLabel, getStaffInitials, siteBindingLabel } from './staffPresentation';
import './StaffPage.css';
import './StaffProfilePage.css';

interface Props {
  staffId: string | null | undefined;
  access: StaffAccess;
  onBack: () => void;
  onOpenPendingLicence?: (licenceId: string) => void;
}

const parseDateValue = (dateStr: string | null | undefined): Date | null => {
  if (!dateStr) return null;
  const normalizedValue = /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
    ? `${dateStr}T00:00:00`
    : dateStr;
  const parsedDate = new Date(normalizedValue);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatDate = (dateStr: string | null | undefined): string => {
  const parsedDate = parseDateValue(dateStr);
  if (!parsedDate) return t('N/A');
  return parsedDate.toLocaleDateString(getLocale(), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const getTodayStart = (): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const MAX_NAME_LENGTH = 80;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EVERY_SITE: SiteBindingSelection = { clientContractIds: [], siteIds: [] };

const compareDates = (left: string | null | undefined, right: string | null | undefined): number => {
  const leftTime = parseDateValue(left)?.getTime() ?? 0;
  const rightTime = parseDateValue(right)?.getTime() ?? 0;
  return leftTime - rightTime;
};

const getLicenceLabel = (licence: Pick<StaffLicenceRow, 'authority' | 'licence_type_name'>): string =>
  licence.licence_type_name?.trim() || t('{authority} licence', { authority: licence.authority });

const getLicenceTimingLabel = (expiryDateStr: string | null | undefined): string => {
  const expiryDate = parseDateValue(expiryDateStr);
  if (!expiryDate) return t('Expiry timing unavailable');

  const dayDifference = Math.round((expiryDate.getTime() - getTodayStart().getTime()) / MS_PER_DAY);

  if (dayDifference > 1) return t('{count} days remaining', { count: dayDifference });
  if (dayDifference === 1) return t('1 day remaining');
  if (dayDifference === 0) return t('Expires today');
  if (dayDifference === -1) return t('Expired 1 day ago');
  return t('Expired {count} days ago', { count: Math.abs(dayDifference) });
};

export default function StaffProfilePage({ staffId, access, onBack, onOpenPendingLicence }: Props) {
  useI18n();
  const [profile, setProfile] = useState<StaffRow | null>(null);
  const [licences, setLicences] = useState<StaffLicenceRow[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [licenceError, setLicenceError] = useState('');
  const [toast, setToast] = useState('');
  const [isConfirmingDeactivate, setIsConfirmingDeactivate] = useState(false);
  // null until the role editor has been opened and its roles have loaded.
  const [editableRoles, setEditableRoles] = useState<CompanyRole[] | null>(null);
  const [isEditingRoles, setIsEditingRoles] = useState(false);
  const [roleDraft, setRoleDraft] = useState<string[]>([]);
  const [roleError, setRoleError] = useState('');
  const [isEditingContract, setIsEditingContract] = useState(false);
  const [contractDraft, setContractDraft] = useState<ContractHoursBand | null>(null);
  const [eroExceptionKind, setEroExceptionKind] = useState<EroMinimumHoursExceptionKind | ''>('');
  const [eroExceptionNote, setEroExceptionNote] = useState('');
  const [contractError, setContractError] = useState('');
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [detailsDraft, setDetailsDraft] = useState({ firstName: '', lastName: '', phone: '' });
  const [detailsError, setDetailsError] = useState('');
  const [isResettingEmail, setIsResettingEmail] = useState(false);
  const [resetEmailDraft, setResetEmailDraft] = useState('');
  const [resetError, setResetError] = useState('');
  const [isEditingSites, setIsEditingSites] = useState(false);
  // null until the sites editor has been opened and the company's clients and sites have loaded.
  const [bindingOptions, setBindingOptions] = useState<SiteBindingOptions | null>(null);
  const [limitSites, setLimitSites] = useState(false);
  const [bindingDraft, setBindingDraft] = useState<SiteBindingSelection>(EVERY_SITE);
  const [bindingError, setBindingError] = useState('');

  const loadProfile = useCallback(async () => {
    setLicenceError('');

    if (!staffId) {
      setProfile(null);
      setLicences([]);
      setError(t('Could not load this profile.'));
      return;
    }

    try {
      const [member, userId] = await Promise.all([
        staffService.fetchStaffMember(staffId),
        staffService.getCurrentUserId(),
      ]);
      if (!member) throw new Error('Staff member not found');

      setProfile(member);
      setCurrentUserId(userId);
      setError('');

      // Licences belong to whoever works shifts, including a guard who also manages.
      if (!member.is_guard) {
        setLicences([]);
        return;
      }

      try {
        setLicences(await staffService.fetchLicences(staffId));
      } catch (licenceLoadError) {
        setLicences([]);
        setLicenceError(t('Could not load licence details.'));
        console.error('Error loading licences:', (licenceLoadError as Error).message);
      }
    } catch (err) {
      setProfile(null);
      setLicences([]);
      setError(t('Could not load this profile.'));
      console.error('Error loading staff profile:', (err as Error).message);
    }
  }, [staffId]);

  useEffect(() => {
    setIsLoading(true);
    void loadProfile().finally(() => setIsLoading(false));
  }, [loadProfile]);

  /* Deactivating a leaver has to be something a company can do itself:
     billing counts distinct users who were active, and a departed member of
     staff who stays active is a number the customer cannot correct without
     emailing us. It is not a deletion, so the shifts they worked, and the
     periods already measured from them, are untouched. */
  const setAccountStatus = async (status: StaffStatus) => {
    if (!profile) return;
    setIsBusy(true);
    setActionError('');
    setToast('');
    try {
      await staffService.setAccountStatus(profile.id, status);
      setIsConfirmingDeactivate(false);
      await loadProfile();
    } catch (err) {
      setIsConfirmingDeactivate(false);
      setActionError((err as Error).message || t('Could not change this account.'));
    } finally {
      setIsBusy(false);
    }
  };

  const openRoleEditor = async () => {
    if (!profile) return;
    setRoleError('');
    setRoleDraft(profile.roles.map((role) => role.id));
    setIsEditingRoles(true);

    if (editableRoles) return;
    try {
      setEditableRoles(assignableRoles(access, await roleService.fetchRoles()));
    } catch (err) {
      setEditableRoles([]);
      setRoleError((err as Error).message || t('Could not load roles.'));
    }
  };

  const saveRoles = async () => {
    if (!profile) return;
    if (roleDraft.length === 0) {
      setRoleError(t('Choose at least one role'));
      return;
    }

    setIsBusy(true);
    setRoleError('');
    setToast('');
    try {
      await roleService.setStaffRoles(profile.id, roleDraft);
      setIsEditingRoles(false);
      setToast(t('Roles updated.'));
      await loadProfile();
    } catch (err) {
      setRoleError((err as Error).message || t('Could not change their roles.'));
    } finally {
      setIsBusy(false);
    }
  };

  const openContractEditor = () => {
    if (!profile) return;
    setContractError('');
    setContractDraft(profile.contract_hours);
    setEroExceptionKind(profile.ero_minimum_hours_exception_kind ?? '');
    setEroExceptionNote(profile.ero_minimum_hours_exception_note ?? '');
    setIsEditingContract(true);
  };

  const saveContractHours = async () => {
    if (!profile) return;
    if (!contractDraft) {
      setContractError(t('Choose their contract hours'));
      return;
    }
    if (requiresEroException && !eroExceptionKind) {
      setContractError(t('Choose the documented ERO exception for a contract below 24 hours.'));
      return;
    }
    if (requiresEroException && eroExceptionNote.trim().length < 10) {
      setContractError(t('Add a clear note showing why the ERO exception applies.'));
      return;
    }

    setIsBusy(true);
    setContractError('');
    setToast('');
    try {
      await staffService.setContractHours(
        profile.id,
        contractDraft,
        requiresEroException && eroExceptionKind
          ? { kind: eroExceptionKind, note: eroExceptionNote }
          : null,
      );
      setIsEditingContract(false);
      setToast(t('Contract hours saved.'));
      await loadProfile();
    } catch (err) {
      setContractError((err as Error).message || t('Could not save their contract hours.'));
    } finally {
      setIsBusy(false);
    }
  };

  const openDetailsEditor = () => {
    if (!profile) return;
    setDetailsError('');
    setDetailsDraft({ ...splitFullName(profile.full_name), phone: profile.phone ?? '' });
    setIsEditingDetails(true);
  };

  const saveDetails = async () => {
    if (!profile) return;

    const firstName = sanitizeText(detailsDraft.firstName, MAX_NAME_LENGTH);
    const lastName = sanitizeText(detailsDraft.lastName, MAX_NAME_LENGTH);
    const phone = phoneDigits(detailsDraft.phone);

    if (!firstName || !lastName) {
      setDetailsError(t('First name and last name are required'));
      return;
    }
    if (phone && !isValidPhone(phone)) {
      setDetailsError(t('Enter a valid phone number'));
      return;
    }
    // One who has none yet (invited, not set up) is not asked for it here.
    if (!phone && profile.is_guard && profile.phone) {
      setDetailsError(t('A guard needs a phone number so they can be reached on shift'));
      return;
    }

    const current = splitFullName(profile.full_name);
    if (firstName === current.firstName && lastName === current.lastName && phone === (profile.phone ?? '')) {
      setIsEditingDetails(false);
      return;
    }

    setIsBusy(true);
    setDetailsError('');
    setToast('');
    try {
      await staffService.updateDetails(profile.id, { firstName, lastName, phone });
      setIsEditingDetails(false);
      setToast(t('Details saved.'));
      await loadProfile();
    } catch (err) {
      setDetailsError((err as Error).message || t('Could not save their details.'));
    } finally {
      setIsBusy(false);
    }
  };

  const openEmailReset = () => {
    if (!profile) return;
    setResetError('');
    setResetEmailDraft('');
    setIsResettingEmail(true);
  };

  /* Nobody changes somebody else's sign-in. This sends a link to the new
     address; the email moves when its owner opens it and confirms with their
     own password, and not before. */
  const sendEmailReset = async (address: string) => {
    if (!profile) return;
    const reportError = isResettingEmail ? setResetError : setActionError;

    const email = sanitizeEmail(address);
    if (!EMAIL_PATTERN.test(email)) {
      reportError(t('A valid email address is required'));
      return;
    }
    if (email === (profile.email ?? '')) {
      reportError(t('That is already the email they sign in with'));
      return;
    }

    setIsBusy(true);
    setResetError('');
    setActionError('');
    setToast('');
    try {
      const result = await staffService.requestEmailReset(profile.id, email);
      setIsResettingEmail(false);
      setToast(result.requiresPassword
        ? t('A reset link has been sent to {email}. Their sign-in does not change until they confirm it.', { email: result.email })
        : t('A link has been sent to {email}. Opening it takes them on to finish setting up.', { email: result.email }));
      await loadProfile();
    } catch (err) {
      reportError((err as Error).message || t('Could not send the reset link.'));
      // A link that could not be delivered still leaves the request open.
      void loadProfile();
    } finally {
      setIsBusy(false);
    }
  };

  const cancelEmailReset = async () => {
    if (!profile) return;
    setIsBusy(true);
    setActionError('');
    setToast('');
    try {
      await staffService.cancelEmailReset(profile.id);
      setToast(t('Email reset cancelled. Its link no longer works.'));
      await loadProfile();
    } catch (err) {
      setActionError((err as Error).message || t('Could not cancel the reset.'));
    } finally {
      setIsBusy(false);
    }
  };

  const openSitesEditor = async () => {
    if (!profile) return;
    setBindingError('');
    setBindingDraft(toBindingSelection(profile.site_bindings));
    setLimitSites(profile.site_bindings.length > 0);
    setIsEditingSites(true);

    if (bindingOptions) return;
    try {
      setBindingOptions(await staffService.fetchSiteBindingOptions());
    } catch (err) {
      setBindingError((err as Error).message || t('Could not load clients and sites.'));
    }
  };

  const saveSites = async () => {
    if (!profile) return;
    const selection = limitSites ? bindingDraft : EVERY_SITE;
    if (limitSites && selection.clientContractIds.length + selection.siteIds.length === 0) {
      setBindingError(t('Choose at least one client or site, or leave them available to all sites.'));
      return;
    }

    setIsBusy(true);
    setBindingError('');
    setToast('');
    try {
      await staffService.setSiteBindings(profile.id, selection);
      setIsEditingSites(false);
      setToast(limitSites
        ? t('Sites saved. Auto scheduling will only give them shifts there.')
        : t('Sites saved. They can be rostered at every site.'));
      await loadProfile();
    } catch (err) {
      setBindingError((err as Error).message || t('Could not save where they work.'));
    } finally {
      setIsBusy(false);
    }
  };

  const resendInvite = async () => {
    if (!profile) return;
    setIsBusy(true);
    setActionError('');
    setToast('');
    try {
      const result = await staffService.resendInvite(profile.id);
      setToast(t('A new invite has been sent to {email}. The earlier link no longer works.', { email: result.email }));
      await loadProfile();
    } catch (err) {
      setActionError((err as Error).message || t('Failed to send invite'));
    } finally {
      setIsBusy(false);
    }
  };

  const { visibleLicences, pendingLicences, showExpiredFallback } = useMemo(() => {
    const todayStart = getTodayStart();

    const validApprovedLicences = licences
      .filter((licence) => licence.status === 'approved')
      .filter((licence) => {
        const expiryDate = parseDateValue(licence.expiry_date);
        return expiryDate !== null && expiryDate >= todayStart;
      })
      .sort((left, right) => compareDates(left.expiry_date, right.expiry_date));

    const expiredLicences = licences
      .filter((licence) => licence.status === 'approved' || licence.status === 'expired')
      .filter((licence) => {
        if (licence.status === 'expired') return true;
        const expiryDate = parseDateValue(licence.expiry_date);
        return expiryDate !== null && expiryDate < todayStart;
      })
      .sort((left, right) => compareDates(right.expiry_date, left.expiry_date));

    const pendingReviewLicences = licences
      .filter((licence) => licence.status === 'pending_review')
      .sort((left, right) => compareDates(right.submitted_at, left.submitted_at));

    return {
      visibleLicences: validApprovedLicences.length > 0 ? validApprovedLicences : expiredLicences.slice(0, 1),
      pendingLicences: pendingReviewLicences,
      showExpiredFallback: validApprovedLicences.length === 0 && expiredLicences.length > 0,
    };
  }, [licences]);

  if (isLoading) return <div className="loading"><div className="spinner" /></div>;

  const avatarUrl = staffService.getAvatarUrl(profile?.avatar_url);
  const displayName = profile?.full_name || profile?.email || t('Unknown');
  const isDisabled = profile?.status === 'disabled';
  const awaitingSetup = Boolean(profile && !isDisabled && isAwaitingSetup(profile));
  const canChangeStatus = Boolean(profile && canChangeAccountStatus(access, currentUserId, profile));
  const canResendInvite = Boolean(profile && awaitingSetup && mayResendInvite(access, profile));
  const canEditRoles = Boolean(profile && !isDisabled && canEditRolesOf(access, profile));
  const canEditDetails = Boolean(profile && !isDisabled && canEditDetailsOf(access, profile));
  const emailReset = profile && !isDisabled ? profile.email_reset : null;
  const missingContractHours = Boolean(profile && missingProfileDetails(profile).includes('contract_hours'));
  const requiresEroException = Boolean(profile && contractDraft && hasIrishEroMinimumHoursIssue({
    ...profile,
    contract_hours: contractDraft,
    ero_minimum_hours_exception_kind: null,
    ero_minimum_hours_exception_note: null,
  }));

  // The two roles nobody is offered the chance to untick, each with its reason.
  // set_staff_roles refuses both; saying so beforehand saves the round trip.
  const lockedRoles: Record<string, string> = {};
  const administratorRole = (editableRoles ?? []).find((role) => role.systemKey === 'system_admin');
  if (profile && administratorRole && profile.roles.some((role) => role.id === administratorRole.id)) {
    if (profile.is_owner) {
      lockedRoles[administratorRole.id] = t('The System Owner is always a system administrator. Transfer ownership first.');
    } else if (profile.id === currentUserId) {
      lockedRoles[administratorRole.id] = t('You cannot remove your own System Administrator role.');
    }
  }

  return (
    <div className="page--full">
      <div className="staff-profile__header">
        <button className="btn btn--secondary" onClick={onBack}>{t('Back')}</button>
        <h1 className="page__title">{t('Staff Profile')}</h1>
      </div>

      {error && <div className="msg--error">{error}</div>}

      {profile && (
        <div className="staff-profile__content">
          <div className="staff-profile__hero">
            <div
              className="staff-profile__avatar"
              style={avatarUrl ? undefined : getAvatarAccentStyle(profile.id || profile.full_name)}
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="avatar__img" />
              ) : (
                getStaffInitials(profile.full_name, profile.email)
              )}
            </div>
            <div>
              <h2 className="staff-profile__name">{displayName}</h2>
              <div className="staff-profile__badges">
                <RoleChips roles={profile.roles} tier={profile.role} isOwner={profile.is_owner} />
                {isDisabled ? (
                  <span className="status-chip status-chip--neutral">{t('Deactivated')}</span>
                ) : profile.invite_status === 'expired' ? (
                  <span className="status-chip status-chip--warning">{t('Invite expired')}</span>
                ) : awaitingSetup ? (
                  <span className="status-chip status-chip--info">{t('Invite sent')}</span>
                ) : (
                  <span className="status-chip status-chip--success">{t('Active')}</span>
                )}
                {missingContractHours && (
                  <span className="status-chip status-chip--warning">{t('Missing details')}</span>
                )}
              </div>
            </div>
            {canEditDetails && (
              <button
                type="button"
                className="btn btn--secondary btn--sm staff-profile__edit"
                onClick={openDetailsEditor}
                disabled={isBusy}
              >
                <FiEdit2 aria-hidden="true" />
                {t('Edit details')}
              </button>
            )}
          </div>

          <div aria-live="polite">
            {toast && <div className="msg--success">{toast}</div>}
            {actionError && <div className="msg--error" role="alert">{actionError}</div>}
          </div>

          {emailReset && (
            <div className="card staff-profile__invite">
              <div>
                <div className="staff-profile__invite-title">
                  {emailReset.expired
                    ? t('Their email reset link no longer works')
                    : t('Waiting for them to confirm a new email')}
                </div>
                <p className="staff-profile__invite-text">
                  {emailReset.expired
                    ? t('The link sent to {email} has expired or was locked. They still sign in with {current}. Send it again, or cancel the reset.', {
                        email: emailReset.email,
                        current: profile.email ?? '',
                      })
                    : !emailReset.lastSentAt
                      ? t('The link for {email} could not be delivered. They still sign in with {current}. Send it again.', {
                          email: emailReset.email,
                          current: profile.email ?? '',
                        })
                      : awaitingSetup
                        ? t('A link was emailed to {email} on {sent}. Opening it takes them on to finish setting up. The invite sent to {current} no longer works.', {
                            email: emailReset.email,
                            sent: formatDate(emailReset.lastSentAt),
                            current: profile.email ?? '',
                          })
                        : t('A link was emailed to {email} on {sent}. They keep signing in with {current} until they open it and confirm with their password.', {
                            email: emailReset.email,
                            sent: formatDate(emailReset.lastSentAt),
                            current: profile.email ?? '',
                          })}
                </p>
              </div>
              {canEditDetails && (
                <div className="staff-profile__reset-actions">
                  <button
                    type="button"
                    className="btn btn--secondary staff-profile__invite-btn"
                    onClick={() => { void sendEmailReset(emailReset.email); }}
                    disabled={isBusy}
                  >
                    <FiSend aria-hidden="true" />
                    {t('Send again')}
                  </button>
                  <button
                    type="button"
                    className="btn btn--secondary"
                    onClick={() => { void cancelEmailReset(); }}
                    disabled={isBusy}
                  >
                    {t('Cancel reset')}
                  </button>
                </div>
              )}
            </div>
          )}

          {awaitingSetup && !emailReset && (
            <div className="card staff-profile__invite">
              <div>
                <div className="staff-profile__invite-title">
                  {profile.invite_status === 'expired'
                    ? t('Their invite link has expired')
                    : t('Waiting for them to finish setting up')}
                </div>
                <p className="staff-profile__invite-text">
                  {profile.invite_status === 'expired'
                    ? t('The link expired on {date}. Send a new invite so they can choose a password and sign in.', {
                        date: formatDate(profile.invite_expires_at),
                      })
                    : profile.invite_last_sent_at
                      ? t('Invite emailed on {sent}. The link works until {expires}. They can already be rostered.', {
                          sent: formatDate(profile.invite_last_sent_at),
                          expires: formatDate(profile.invite_expires_at),
                        })
                      : t('The account exists, but the invite email has not been delivered yet. Send it again so they can sign in.')}
                </p>
              </div>
              {canResendInvite && (
                <button
                  type="button"
                  className="btn btn--secondary staff-profile__invite-btn"
                  onClick={() => { void resendInvite(); }}
                  disabled={isBusy}
                >
                  <FiSend aria-hidden="true" />
                  {t('Resend invite')}
                </button>
              )}
            </div>
          )}

          {missingContractHours && (
            <div className="card staff-profile__missing">
              <FiAlertCircle className="staff-profile__missing-icon" aria-hidden="true" />
              <div>
                <div className="staff-profile__missing-title">{t('Missing profile details')}</div>
                <p className="staff-profile__missing-text">
                  {t('Their contract hours are not set. Guards who join with a join code start without them, and auto scheduling cannot give them their contracted hours until they are.')}
                </p>
              </div>
              <button
                type="button"
                className="btn btn--primary staff-profile__missing-btn"
                onClick={openContractEditor}
                disabled={isBusy}
              >
                {t('Set contract hours')}
              </button>
            </div>
          )}

          <div className="card staff-profile__details">
            <div className="staff-profile__row">
              <span className="form__label">{t('Email')}</span>
              <span className="staff-profile__roles">
                <span className="text-muted">{profile.email || t('N/A')}</span>
                {canEditDetails && profile.email && !emailReset && (
                  <button
                    type="button"
                    className="btn btn--secondary btn--sm"
                    onClick={openEmailReset}
                    disabled={isBusy}
                  >
                    {t('Reset email')}
                  </button>
                )}
              </span>
            </div>
            <div className="staff-profile__row">
              <span className="form__label">{t('Phone')}</span>
              <span className="text-muted">{profile.phone || t('N/A')}</span>
            </div>
            <div className="staff-profile__row">
              <span className="form__label">{t('Roles')}</span>
              <span className="staff-profile__roles">
                <RoleChips roles={profile.roles} tier={profile.role} isOwner={profile.is_owner} />
                {canEditRoles && (
                  <button
                    type="button"
                    className="btn btn--secondary btn--sm"
                    onClick={() => { void openRoleEditor(); }}
                    disabled={isBusy}
                  >
                    {t('Change roles')}
                  </button>
                )}
              </span>
            </div>
            {profile.is_guard && (
              <div className="staff-profile__row">
                <span className="form__label">{t('Contract hours')}</span>
                <span className="staff-profile__roles">
                  <span className={profile.contract_hours ? 'text-muted' : 'staff-profile__unset'}>
                    {profile.contract_hours ? contractBandLabel(profile.contract_hours) : t('Not set')}
                  </span>
                  {!isDisabled && (
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      onClick={openContractEditor}
                      disabled={isBusy}
                    >
                      {profile.contract_hours ? t('Change') : t('Set contract hours')}
                    </button>
                  )}
                </span>
              </div>
            )}
            {profile.is_guard && (
              <div className="staff-profile__row staff-profile__row--top">
                <span className="form__label">{t('Works at')}</span>
                <span className="staff-profile__sites">
                  {profile.site_bindings.length === 0 ? (
                    <span className="staff-profile__sites-all">
                      <span className="text-muted">{t('All sites')}</span>
                      {!isDisabled && (
                        <span className="staff-profile__sites-hint">
                          {t('Recommended: choose the clients or sites they work, so auto scheduling keeps them there.')}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="staff-profile__site-chips">
                      {profile.site_bindings.map((binding) => (
                        <span
                          key={`${binding.kind}-${binding.id}`}
                          className={`staff-profile__site-chip${binding.archived ? ' staff-profile__site-chip--archived' : ''}`}
                          title={binding.kind === 'site' && binding.clientName ? binding.clientName : undefined}
                        >
                          {siteBindingLabel(binding)}
                        </span>
                      ))}
                    </span>
                  )}
                  {!isDisabled && (
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      onClick={() => { void openSitesEditor(); }}
                      disabled={isBusy}
                    >
                      {profile.site_bindings.length > 0 ? t('Change') : t('Choose sites')}
                    </button>
                  )}
                </span>
              </div>
            )}
            {profile.ero_minimum_hours_exception_kind && profile.ero_minimum_hours_exception_note && (
              <div className="staff-profile__row staff-profile__row--top">
                <span className="form__label">{t('ERO minimum-hours exception')}</span>
                <span className="text-muted">
                  {profile.ero_minimum_hours_exception_kind === 'operational_hours_below_24'
                    ? t('Demonstrably fewer operational hours')
                    : t('Subsisting employer/worker arrangement')}
                  {`: ${profile.ero_minimum_hours_exception_note}`}
                </span>
              </div>
            )}
            <div className="staff-profile__row">
              <span className="form__label">{t('Member Since')}</span>
              <span className="text-muted">{formatDate(profile.created_at)}</span>
            </div>
            {profile.is_guard && (
              <div className="staff-profile__row staff-profile__row--top">
                <span className="form__label">{t('Licences')}</span>
                <div className="staff-profile__licence-column">
                  {licenceError ? (
                    <span className="text-muted">{licenceError}</span>
                  ) : (
                    <>
                      {showExpiredFallback && (
                        <p className="staff-profile__licence-note">
                          {t('No valid licences found. Showing the most recently expired licence.')}
                        </p>
                      )}

                      {visibleLicences.length > 0 && (
                        <div className="staff-profile__licence-list">
                          {visibleLicences.map((licence) => (
                            <div key={licence.id} className="staff-profile__licence-item">
                              <div className="staff-profile__licence-item-header">
                                <span className="staff-profile__licence-name">{getLicenceLabel(licence)}</span>
                                <span
                                  className={`staff-profile__licence-badge ${
                                    showExpiredFallback
                                      ? 'staff-profile__licence-badge--expired'
                                      : 'staff-profile__licence-badge--valid'
                                  }`}
                                >
                                  {showExpiredFallback ? 'Expired' : 'Valid'}
                                </span>
                              </div>
                              <div className="staff-profile__licence-meta">
                                <span className="staff-profile__licence-number">{licence.licence_number}</span>
                                <span>{licence.authority}</span>
                                <span>{`${showExpiredFallback ? 'Expired' : 'Expires'} ${formatDate(licence.expiry_date)}`}</span>
                                <span className="staff-profile__licence-timing">{getLicenceTimingLabel(licence.expiry_date)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {pendingLicences.length > 0 && (
                        <div className="staff-profile__pending">
                          <span className="staff-profile__pending-title">{t('Pending approval')}</span>
                          <div className="staff-profile__pending-list">
                            {pendingLicences.map((licence) => (
                              <div key={licence.id} className="staff-profile__pending-item">
                                {onOpenPendingLicence ? (
                                  <button
                                    type="button"
                                    className="staff-profile__pending-link"
                                    onClick={() => onOpenPendingLicence(licence.id)}
                                  >
                                    Review {getLicenceLabel(licence)}
                                  </button>
                                ) : (
                                  <span className="staff-profile__pending-link staff-profile__pending-link--static">
                                    {getLicenceLabel(licence)}
                                  </span>
                                )}
                                <span className="staff-profile__pending-meta">
                                  {`Licence ${licence.licence_number}, submitted ${formatDate(licence.submitted_at)}`}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {visibleLicences.length === 0 && pendingLicences.length === 0 && (
                        <span className="text-muted">
                          {t('This guard currently has no licence or licence request.')}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {canChangeStatus && (
            <div className="card staff-profile__account">
              <div>
                <div className="staff-profile__account-title">{t('Account status')}</div>
                <p className="staff-profile__account-text">
                  {isDisabled
                    ? t('This account is deactivated and cannot sign in. Reactivate it to restore their access.')
                    : t('Deactivate someone who has left. They will no longer be able to sign in. Their shift history is kept, and you can reactivate them at any time.')}
                </p>
              </div>
              {isDisabled ? (
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => { void setAccountStatus('active'); }}
                  disabled={isBusy}
                >
                  {t('Reactivate')}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn--danger"
                  onClick={() => setIsConfirmingDeactivate(true)}
                  disabled={isBusy}
                >
                  {t('Deactivate')}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {profile && isEditingRoles && (
        <div className="modal-overlay">
          <div className="modal staff-form" role="dialog" aria-modal="true" aria-labelledby="staff-roles-title">
            <div className="staff-form__head">
              <div>
                <h2 className="modal__title" id="staff-roles-title">
                  {t('Roles for {name}', { name: displayName })}
                </h2>
                <p className="staff-form__lead">
                  {t('Their roles add up: they can open every area any of their roles opens.')}
                </p>
              </div>
            </div>

            <div aria-live="polite">
              {roleError && <div className="msg--error" role="alert">{roleError}</div>}
            </div>

            {editableRoles === null ? (
              <p className="staff-form__hint">{t('Loading roles...')}</p>
            ) : (
              <RolePicker
                idPrefix="staff-roles"
                roles={editableRoles}
                selectedIds={roleDraft}
                onChange={setRoleDraft}
                disabled={isBusy}
                locked={lockedRoles}
              />
            )}

            <div className="staff-form__footer">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setIsEditingRoles(false)}
                disabled={isBusy}
              >
                {t('Cancel')}
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => { void saveRoles(); }}
                disabled={isBusy || editableRoles === null}
              >
                {isBusy ? t('Saving...') : t('Save roles')}
              </button>
            </div>
          </div>
        </div>
      )}

      {profile && isEditingContract && (
        <div className="modal-overlay">
          <div className="modal staff-form" role="dialog" aria-modal="true" aria-labelledby="staff-contract-title">
            <div className="staff-form__head">
              <div>
                <h2 className="modal__title" id="staff-contract-title">
                  {t('Contract hours for {name}', { name: displayName })}
                </h2>
                <p className="staff-form__lead">
                  {t('The weekly hours they are guaranteed. Auto scheduling gives guards at least the bottom of their band where the rules allow.')}
                </p>
              </div>
            </div>

            <div aria-live="polite">
              {contractError && <div className="msg--error" role="alert">{contractError}</div>}
            </div>

            <div className="form__group">
              <label className="form__label" htmlFor="staff-contract-hours">{t('Contract hours')}</label>
              <ContractHoursSelect
                id="staff-contract-hours"
                value={contractDraft}
                onChange={setContractDraft}
                disabled={isBusy}
                invalid={Boolean(contractError) && !contractDraft}
              />
            </div>

            {requiresEroException && (
              <>
                <div className="msg--warning" role="note">
                  {t('After six months, an Irish security operative must normally be guaranteed at least 24 hours per week. A lower band requires a documented ERO exception.')}
                </div>
                <div className="form__group">
                  <label className="form__label" htmlFor="staff-ero-hours-exception">
                    {t('ERO exception')}
                  </label>
                  <select
                    id="staff-ero-hours-exception"
                    className="form__select"
                    value={eroExceptionKind}
                    onChange={(event) => setEroExceptionKind(event.target.value as EroMinimumHoursExceptionKind | '')}
                    disabled={isBusy}
                  >
                    <option value="">{t('Choose an exception')}</option>
                    <option value="operational_hours_below_24">{t('Demonstrably fewer operational hours')}</option>
                    <option value="subsisting_arrangement">{t('Subsisting employer/worker arrangement')}</option>
                  </select>
                </div>
                <div className="form__group">
                  <label className="form__label" htmlFor="staff-ero-hours-note">
                    {t('Exception evidence note')}
                  </label>
                  <textarea
                    id="staff-ero-hours-note"
                    className="form__textarea"
                    value={eroExceptionNote}
                    onChange={(event) => setEroExceptionNote(event.target.value)}
                    placeholder={t('Record the operational evidence or the date and parties to the subsisting arrangement.')}
                    disabled={isBusy}
                    rows={3}
                  />
                </div>
              </>
            )}

            <div className="staff-form__footer">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setIsEditingContract(false)}
                disabled={isBusy}
              >
                {t('Cancel')}
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => { void saveContractHours(); }}
                disabled={isBusy}
              >
                {isBusy ? t('Saving...') : t('Save contract hours')}
              </button>
            </div>
          </div>
        </div>
      )}

      {profile && isEditingDetails && (
        <div className="modal-overlay">
          <form
            className="modal staff-form"
            role="dialog"
            aria-modal="true"
            aria-labelledby="staff-details-title"
            onSubmit={(event) => { event.preventDefault(); void saveDetails(); }}
            noValidate
          >
            <div className="staff-form__head">
              <div>
                <h2 className="modal__title" id="staff-details-title">
                  {t('Details for {name}', { name: displayName })}
                </h2>
                <p className="staff-form__lead">
                  {t('Correct their name or their phone number. The email they sign in with is changed with Reset email.')}
                </p>
              </div>
            </div>

            <div aria-live="polite">
              {detailsError && <div className="msg--error" role="alert">{detailsError}</div>}
            </div>

            <div className="staff-form__grid">
              <div className="form__group">
                <label className="form__label" htmlFor="staff-details-first-name">{t('First name')}</label>
                <input
                  id="staff-details-first-name"
                  type="text"
                  className="form__input"
                  value={detailsDraft.firstName}
                  onChange={(event) => setDetailsDraft({ ...detailsDraft, firstName: event.target.value })}
                  maxLength={MAX_NAME_LENGTH}
                  autoComplete="off"
                  disabled={isBusy}
                />
              </div>
              <div className="form__group">
                <label className="form__label" htmlFor="staff-details-last-name">{t('Last name')}</label>
                <input
                  id="staff-details-last-name"
                  type="text"
                  className="form__input"
                  value={detailsDraft.lastName}
                  onChange={(event) => setDetailsDraft({ ...detailsDraft, lastName: event.target.value })}
                  maxLength={MAX_NAME_LENGTH}
                  autoComplete="off"
                  disabled={isBusy}
                />
              </div>
            </div>

            <div className="form__group">
              <label className="form__label" htmlFor="staff-details-phone">{t('Phone')}</label>
              <input
                id="staff-details-phone"
                type="tel"
                inputMode="tel"
                className="form__input"
                value={detailsDraft.phone}
                onChange={(event) => setDetailsDraft({ ...detailsDraft, phone: event.target.value })}
                aria-describedby="staff-details-phone-hint"
                autoComplete="off"
                disabled={isBusy}
              />
              <p className="staff-form__hint" id="staff-details-phone-hint">
                {t('Include the country code. Only the digits are kept.')}
              </p>
            </div>

            <div className="staff-form__footer">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setIsEditingDetails(false)}
                disabled={isBusy}
              >
                {t('Cancel')}
              </button>
              <button type="submit" className="btn btn--primary" disabled={isBusy}>
                {isBusy ? t('Saving...') : t('Save details')}
              </button>
            </div>
          </form>
        </div>
      )}

      {profile && isResettingEmail && (
        <div className="modal-overlay">
          <form
            className="modal staff-form"
            role="dialog"
            aria-modal="true"
            aria-labelledby="staff-email-reset-title"
            onSubmit={(event) => { event.preventDefault(); void sendEmailReset(resetEmailDraft); }}
            noValidate
          >
            <div className="staff-form__head">
              <div>
                <h2 className="modal__title" id="staff-email-reset-title">
                  {t('Reset email for {name}', { name: displayName })}
                </h2>
                <p className="staff-form__lead">
                  {awaitingSetup
                    ? t('A link is sent to the new address. They have not set up yet, so opening it takes them on to choose a password, and the invite already sent stops working.')
                    : t('A link is sent to the new address. Their sign-in does not change until they open it and confirm with their current password.')}
                </p>
              </div>
            </div>

            <div aria-live="polite">
              {resetError && <div className="msg--error" role="alert">{resetError}</div>}
            </div>

            <div className="form__group">
              <label className="form__label" htmlFor="staff-email-reset-address">{t('New email')}</label>
              <input
                id="staff-email-reset-address"
                type="email"
                className="form__input"
                value={resetEmailDraft}
                onChange={(event) => setResetEmailDraft(event.target.value)}
                aria-describedby="staff-email-reset-hint"
                autoComplete="off"
                disabled={isBusy}
              />
              <p className="staff-form__hint" id="staff-email-reset-hint">
                {t('They sign in with {email} today.', { email: profile.email ?? '' })}
              </p>
            </div>

            <div className="staff-form__footer">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setIsResettingEmail(false)}
                disabled={isBusy}
              >
                {t('Cancel')}
              </button>
              <button type="submit" className="btn btn--primary staff-form__submit" disabled={isBusy}>
                <FiSend aria-hidden="true" />
                {isBusy ? t('Sending...') : t('Send reset link')}
              </button>
            </div>
          </form>
        </div>
      )}

      {profile && isEditingSites && (
        <div className="modal-overlay">
          <div className="modal staff-form" role="dialog" aria-modal="true" aria-labelledby="staff-sites-title">
            <div className="staff-form__head">
              <div>
                <h2 className="modal__title" id="staff-sites-title">
                  {t('Where {name} works', { name: displayName })}
                </h2>
                <p className="staff-form__lead">
                  {t('A guard can be rostered at every site unless you limit them here. Auto scheduling only gives a limited guard shifts at the clients and sites you choose.')}
                </p>
              </div>
            </div>

            <div aria-live="polite">
              {bindingError && <div className="msg--error" role="alert">{bindingError}</div>}
            </div>

            <fieldset className="staff-sites__mode" disabled={isBusy}>
              <legend className="staff-sites__mode-legend">{t('Sites they can be rostered at')}</legend>
              <label className="staff-sites__mode-option" htmlFor="staff-sites-mode-all">
                <input
                  id="staff-sites-mode-all"
                  type="radio"
                  name="staff-sites-mode"
                  checked={!limitSites}
                  onChange={() => setLimitSites(false)}
                />
                <span className="staff-sites__text">
                  <span className="staff-sites__name">{t('All sites')}</span>
                  <span className="staff-sites__detail">
                    {t('Can be rostered anywhere, including at sites added later.')}
                  </span>
                </span>
              </label>
              <label className="staff-sites__mode-option" htmlFor="staff-sites-mode-chosen">
                <input
                  id="staff-sites-mode-chosen"
                  type="radio"
                  name="staff-sites-mode"
                  checked={limitSites}
                  onChange={() => setLimitSites(true)}
                />
                <span className="staff-sites__text">
                  <span className="staff-sites__name">
                    {t('Chosen clients and sites only')}
                    <span className="staff-sites__tag staff-sites__tag--recommended">{t('Recommended')}</span>
                  </span>
                  <span className="staff-sites__detail">
                    {t('Only given shifts at the places you tick below.')}
                  </span>
                </span>
              </label>
            </fieldset>

            {limitSites && (
              bindingOptions === null ? (
                !bindingError && <p className="staff-form__hint">{t('Loading clients and sites...')}</p>
              ) : (
                <>
                  <SiteBindingPicker
                    idPrefix="staff-sites"
                    options={bindingOptions}
                    value={bindingDraft}
                    onChange={setBindingDraft}
                    disabled={isBusy}
                  />
                  <p className="staff-form__hint">
                    {t('Shifts they already hold anywhere else stay on the roster until you take them off.')}
                  </p>
                </>
              )
            )}

            <div className="staff-form__footer">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setIsEditingSites(false)}
                disabled={isBusy}
              >
                {t('Cancel')}
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => { void saveSites(); }}
                disabled={isBusy || (limitSites && bindingOptions === null)}
              >
                {isBusy ? t('Saving...') : t('Save sites')}
              </button>
            </div>
          </div>
        </div>
      )}

      {profile && isConfirmingDeactivate && (
        <div className="modal-overlay">
          <div className="modal modal--sm" role="alertdialog" aria-modal="true" aria-labelledby="staff-deactivate-title">
            <div className="modal__header">
              <h2 className="modal__title" id="staff-deactivate-title">
                {t('Deactivate {name}?', { name: displayName })}
              </h2>
            </div>
            <p className="text-muted staff-profile__confirm-text">
              {profile.role === 'guard'
                ? t('They will no longer be able to sign in to the app. Shifts they have already worked stay on record, and you can reactivate them at any time.')
                : t('They will no longer be able to sign in to this workspace. You can reactivate them at any time.')}
            </p>
            {profile.is_owner && (
              <p className="text-muted staff-profile__confirm-text">
                {t('They are the System Owner. Ownership will pass to the longest-serving system administrator, and will not return to them if they are reactivated.')}
              </p>
            )}
            <div className="staff-profile__confirm-actions">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setIsConfirmingDeactivate(false)}
                disabled={isBusy}
              >
                {t('Cancel')}
              </button>
              <button
                type="button"
                className="btn btn--danger"
                onClick={() => { void setAccountStatus('disabled'); }}
                disabled={isBusy}
              >
                {isBusy ? t('Deactivating...') : t('Deactivate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
